import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);

  useEffect(() => {
    // Check for existing token on mount
    const savedToken = localStorage.getItem('admin_token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await axios.get(`${API}/verify`, {
        headers: { Authorization: `Bearer ${tokenToVerify}` }
      });
      
      if (response.data.valid) {
        setToken(tokenToVerify);
        setAdmin({ username: response.data.username });
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/login`, {
        username,
        password
      });
      
      const { access_token, username: adminUsername } = response.data;
      
      localStorage.setItem('admin_token', access_token);
      setToken(access_token);
      setAdmin({ username: adminUsername });
      setIsAuthenticated(true);
      
      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    setIsAuthenticated(false);
  };

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  // API helper functions
  const api = {
    // Dashboard
    getDashboardStats: () => axios.get(`${API}/dashboard-stats`, getAuthHeaders()),
    
    // Services
    getServices: () => axios.get(`${API}/services`, getAuthHeaders()),
    createService: (service) => axios.post(`${API}/services`, service, getAuthHeaders()),
    updateService: (id, service) => axios.put(`${API}/services/${id}`, service, getAuthHeaders()),
    deleteService: (id) => axios.delete(`${API}/services/${id}`, getAuthHeaders()),
    
    // Calculator Config
    getCalculatorConfig: () => axios.get(`${API}/calculator-config`, getAuthHeaders()),
    updateCalculatorConfig: (config) => axios.put(`${API}/calculator-config`, config, getAuthHeaders()),
    
    // Company Info
    getCompanyInfo: () => axios.get(`${API}/company-info`, getAuthHeaders()),
    updateCompanyInfo: (info) => axios.put(`${API}/company-info`, info, getAuthHeaders()),
    
    // Contacts
    getContacts: (status) => axios.get(`${API}/contacts${status ? `?status=${status}` : ''}`, getAuthHeaders()),
    updateContactStatus: (id, status) => axios.put(`${API}/contacts/${id}/status?status=${status}`, {}, getAuthHeaders()),
    
    // Password
    changePassword: (currentPassword, newPassword) => 
      axios.post(`${API}/change-password`, { current_password: currentPassword, new_password: newPassword }, getAuthHeaders()),
  };

  return (
    <AdminContext.Provider value={{
      isAuthenticated,
      isLoading,
      admin,
      token,
      login,
      logout,
      api
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
